import {cn} from '@tempmee/core'
import {
  Children,
  type ElementType,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {TabVariantsFactory} from './Variants'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TabsContextValue {
  activeValue: string
  setActiveValue: (value: string) => void
  baseId: string
  fullWidth: boolean
  registerTab: (value: string) => void
  unregisterTab: (value: string) => void
  tabValues: React.MutableRefObject<string[]>
  disabledTabs: React.MutableRefObject<Set<string>>
  registerDisabled: (value: string) => void
  unregisterDisabled: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabsContext() {
  const ctx = useContext(TabsContext)
  if (!ctx) {
    throw new Error('<Tab> and <TabPanel> must be used within a <Tabs> parent.')
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Per-Tab state context — lets anything rendered inside a Tab (a counter badge,
// a state-dependent icon, etc.) read its own tab's state without prop-drilling
// or recomputation. Mirrors MUI's `useFormControl()` (a hook that exposes the
// surrounding stateful component's state to its descendants).
// ---------------------------------------------------------------------------

export interface TabState {
  /** Whether the surrounding tab is currently selected. */
  selected: boolean
  /** Whether the surrounding tab is disabled. */
  disabled: boolean
}

const TabStateContext = createContext<TabState | null>(null)

/**
 * Returns the state of the surrounding `<Tab>` (`{selected, disabled}`).
 *
 * Modeled on MUI's `useFormControl()`. Use inside a Tab's `label`, `children`,
 * or `icon` to drive state-dependent content (e.g. an active/inactive counter
 * color or icon) from a single source of truth, instead of recomputing the
 * active tab at every call site:
 *
 * ```tsx
 * function TabBadge({count}: {count: number}) {
 *   const {selected} = useTabState()
 *   return <Badge severity={selected ? 'info' : 'secondary'} count={count} />
 * }
 * <Tab value="alerts" label={<>Alerts <TabBadge count={3} /></>} />
 * ```
 *
 * @throws if called outside a `<Tab>`.
 */
export function useTabState(): TabState {
  const state = useContext(TabStateContext)
  if (state === null) {
    throw new Error('useTabState() must be called inside a <Tab>.')
  }
  return state
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export interface TabProps extends Omit<HTMLAttributes<HTMLElement>, 'value' | 'children'> {
  /** Unique identifier; matches the corresponding TabPanel value. */
  value: string
  /** Tab label. */
  label?: ReactNode
  /** Optional icon. */
  icon?: ReactNode
  /** Icon placement relative to the label. @default 'start' */
  iconPosition?: 'start' | 'end'
  /** Whether this tab is disabled. */
  disabled?: boolean
  /** Render the tab as a link (`<a href>`) — for real navigation destinations. */
  href?: string
  /**
   * Render the tab as a custom element (e.g. a router `Link`). Any extra props
   * (`to`, `data-*`, …) are forwarded to it. Defaults to `a` when `href` is set,
   * otherwise `button`.
   */
  component?: ElementType
  /** Alternative to `label` — arbitrary tab content. */
  children?: ReactNode
  /** Arbitrary `data-*` attributes (e.g. test hooks) forwarded to the rendered element. */
  [dataAttr: `data-${string}`]: unknown
}

const Tab = forwardRef<HTMLElement, TabProps>(
  (
    {
      value,
      label,
      icon,
      iconPosition = 'start',
      disabled = false,
      href,
      component,
      className,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const ctx = useTabsContext()
    const isActive = ctx.activeValue === value
    const tabId = `${ctx.baseId}-tab-${value}`
    const panelId = `${ctx.baseId}-tabpanel-${value}`

    useEffect(() => {
      ctx.registerTab(value)
      return () => ctx.unregisterTab(value)
    }, [value, ctx])

    useEffect(() => {
      if (disabled) ctx.registerDisabled(value)
      else ctx.unregisterDisabled(value)
      return () => ctx.unregisterDisabled(value)
    }, [disabled, value, ctx])

    const content = label ?? children
    const Comp: ElementType = component ?? (href != null ? 'a' : 'button')
    const isButton = Comp === 'button'
    const tabState = useMemo<TabState>(() => ({selected: isActive, disabled}), [isActive, disabled])

    const handleClick = (e: MouseEvent<HTMLElement>) => {
      if (disabled) {
        e.preventDefault()
        return
      }
      ctx.setActiveValue(value)
      onClick?.(e as never)
    }

    return (
      <Comp
        ref={ref}
        id={tabId}
        role="tab"
        aria-selected={isActive}
        aria-controls={panelId}
        tabIndex={isActive ? 0 : -1}
        onClick={handleClick}
        className={cn(
          TabVariantsFactory({active: isActive, disabled, fullWidth: ctx.fullWidth}),
          disabled && !isButton && 'pointer-events-none',
          className,
        )}
        {...(href != null ? {href} : {})}
        {...(isButton ? {type: 'button', disabled} : {'aria-disabled': disabled || undefined})}
        {...props}
      >
        <TabStateContext.Provider value={tabState}>
          {icon && iconPosition === 'start' && icon}
          {content}
          {icon && iconPosition === 'end' && icon}
        </TabStateContext.Provider>
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-[3px] rounded-full bg-primary"
          />
        )}
      </Comp>
    )
  },
)
Tab.displayName = 'Tab'

// ---------------------------------------------------------------------------
// TabPanel
// ---------------------------------------------------------------------------

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Value matching the corresponding Tab. */
  value: string
  children: ReactNode
}

const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  ({value, className, children, ...props}, ref) => {
    const ctx = useTabsContext()
    const isActive = ctx.activeValue === value
    const panelId = `${ctx.baseId}-tabpanel-${value}`
    const tabId = `${ctx.baseId}-tab-${value}`

    if (!isActive) return null

    return (
      <div
        ref={ref}
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId}
        className={cn('mt-2', className)}
        {...props}
      >
        {children}
      </div>
    )
  },
)
TabPanel.displayName = 'TabPanel'

// ---------------------------------------------------------------------------
// Tabs (root — acts as the tablist; partitions Tab children from TabPanels)
// ---------------------------------------------------------------------------

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Active tab value (controlled). */
  value?: string
  /** Initially active tab (uncontrolled). */
  defaultValue?: string
  /** Called with the new value when the active tab changes. */
  onChange?: (value: string) => void
  /** Stretch tabs to fill the available width. */
  fullWidth?: boolean
  children: ReactNode
}

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(
  ({value, defaultValue, onChange, fullWidth = false, className, children, ...props}, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue ?? '')
    const activeValue = value ?? internalValue
    const baseId = useId()

    const tabValuesRef = useRef<string[]>([])
    const disabledTabsRef = useRef<Set<string>>(new Set())

    const registerTab = useCallback((v: string) => {
      if (!tabValuesRef.current.includes(v)) tabValuesRef.current.push(v)
    }, [])
    const unregisterTab = useCallback((v: string) => {
      tabValuesRef.current = tabValuesRef.current.filter((t) => t !== v)
    }, [])
    const registerDisabled = useCallback((v: string) => {
      disabledTabsRef.current.add(v)
    }, [])
    const unregisterDisabled = useCallback((v: string) => {
      disabledTabsRef.current.delete(v)
    }, [])

    const setActiveValue = useCallback(
      (v: string) => {
        if (value === undefined) setInternalValue(v)
        onChange?.(v)
      },
      [value, onChange],
    )

    const ctx = useMemo<TabsContextValue>(
      () => ({
        activeValue,
        setActiveValue,
        baseId,
        fullWidth,
        registerTab,
        unregisterTab,
        tabValues: tabValuesRef,
        disabledTabs: disabledTabsRef,
        registerDisabled,
        unregisterDisabled,
      }),
      [
        activeValue,
        setActiveValue,
        baseId,
        fullWidth,
        registerTab,
        unregisterTab,
        registerDisabled,
        unregisterDisabled,
      ],
    )

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      const values = tabValuesRef.current
      const currentIdx = values.indexOf(activeValue)
      if (currentIdx === -1) return

      const findNext = (start: number, direction: 1 | -1): number | undefined => {
        let idx = start
        for (let i = 0; i < values.length; i++) {
          idx = (idx + direction + values.length) % values.length
          const val = values[idx]
          if (val && !disabledTabsRef.current.has(val)) return idx
        }
        return undefined
      }

      let nextIdx: number | undefined
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        nextIdx = findNext(currentIdx, 1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        nextIdx = findNext(currentIdx, -1)
      } else if (e.key === 'Home') {
        e.preventDefault()
        nextIdx = findNext(values.length - 1, 1)
      } else if (e.key === 'End') {
        e.preventDefault()
        nextIdx = findNext(0, -1)
      }

      if (nextIdx !== undefined) {
        const nextVal = values[nextIdx]
        if (nextVal !== undefined) {
          setActiveValue(nextVal)
          document.getElementById(`${baseId}-tab-${nextVal}`)?.focus()
        }
      }
    }

    // Partition children: Tabs go in the tablist row, TabPanels render below.
    const tabs: ReactNode[] = []
    const panels: ReactNode[] = []
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.type === TabPanel) panels.push(child)
      else tabs.push(child)
    })

    return (
      <TabsContext.Provider value={ctx}>
        <div ref={ref} className={cn('flex flex-col', className)} {...props}>
          <div
            role="tablist"
            className={cn('flex items-center', fullWidth && 'w-full')}
            onKeyDown={handleKeyDown}
          >
            {tabs}
          </div>
          {panels}
        </div>
      </TabsContext.Provider>
    )
  },
)
TabsRoot.displayName = 'Tabs'

// ---------------------------------------------------------------------------
// Exports — flat API: <Tabs>, <Tab>, <TabPanel>
// ---------------------------------------------------------------------------

export const Tabs = TabsRoot
export {Tab, TabPanel}
